import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { Unidad } from '@/services/unitService';
import { toast } from 'sonner';

interface UnitFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (unit: Unidad) => void;
  initialData?: Unidad | null;
  buildingId: string;
}

const formSchema = z.object({
  numero_unidad: z.string().min(1, { message: "El número de unidad es requerido." }),
  tipo_unidad: z.string().optional(),
  esta_ocupada: z.boolean().default(false).optional(),
});

const UnitForm: React.FC<UnitFormProps> = ({ isOpen, onClose, onSave, initialData, buildingId }) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      numero_unidad: "",
      tipo_unidad: "",
      esta_ocupada: false,
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        numero_unidad: initialData.numero_unidad || "",
        tipo_unidad: initialData.tipo_unidad || "",
        esta_ocupada: initialData.esta_ocupada || false,
      });
    } else {
      form.reset();
    }
  }, [initialData, form]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    onSave({
      ...initialData,
      id_edificio: buildingId,
      numero_unidad: values.numero_unidad,
      tipo_unidad: values.tipo_unidad,
      esta_ocupada: values.esta_ocupada,
    });
    onClose();
    toast.success(initialData ? "Unidad actualizada exitosamente." : "Unidad creada exitosamente.");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData ? "Editar Unidad" : "Agregar Nueva Unidad"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="numero_unidad"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número de Unidad</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej. Apt. 101" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tipo_unidad"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Unidad</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Apartamento">Apartamento</SelectItem>
                      <SelectItem value="Oficina">Oficina</SelectItem>
                      <SelectItem value="Local Comercial">Local Comercial</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="esta_ocupada"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow-sm">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>¿Está Ocupada?</FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full">Guardar</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnitForm;
